/**
 * tokens.ts — TypeScript mirror of the two design-system token sets.
 *
 * These constants restate the *key* palette / type / spacing custom properties that
 * `styles/garnet.css` and `styles/core.css` declare, so components and stories can
 * reference exact hex/scale values type-safely (e.g. inline SVG fills, story canvases,
 * assurance state -> colour assertions). The CSS files remain the runtime source of
 * truth via custom properties; `token-integrity` tests assert these stay in lock-step.
 *
 * Faithful port — do not invent values. Every hex here appears verbatim in the CSS.
 */
// ─────────────────────────── Garnet console (assurance) ───────────────────────────
/** The assurance spine + surfaces (manufacturing/tokens.css). */
export const GARNET_TOKENS = {
    // surface
    ink: '#1a1d24',
    ink2: '#2c303a',
    paper: '#f3f4f2',
    panel: '#ffffff',
    rule: '#d9d9d2',
    ruleSoft: '#e7e8e3',
    muted: '#6b6f76',
    muted2: '#8a8d93',
    // assurance · the spine
    verified: '#1b7a6b', // jade · verified by machine
    verifiedBg: '#e3f0ed',
    verifiedInk: '#0f3d36',
    attest: '#b6862c', // gold · verified by attestation
    attestBg: '#f6ecd4',
    attestInk: '#5e4410',
    flag: '#b3402f', // brick · flag
    flagBg: '#f3ddd7',
    flagInk: '#5e1c12',
    // neutral / unverified — absence, not a negative finding
    neutral: '#8a8d93',
    neutralBg: '#ececec',
    neutralInk: '#3a3d44',
};
/** TPM tier ink-weight ramp — deliberately off the traffic-light. */
export const GARNET_TIER_TOKENS = {
    low: { bg: '#ffffff', line: '#1a1d24', ink: '#1a1d24' },
    standard: { bg: '#e4e5e1', line: '#c4c6c0', ink: '#1a1d24' },
    high: { bg: '#a9aba4', line: '#8a8d86', ink: '#1a1d24' },
    critical: { bg: '#1a1d24', line: '#1a1d24', ink: '#f3f4f2' },
};
/** Depth-gradient cell backgrounds — machine certainty falls left→right. */
export const GARNET_DEPTH_BG = ['#eef4f2', '#f4f1ea', '#f6ece1', '#f5e6df'];
export const GARNET_TYPE = {
    display: "'Space Grotesk', system-ui, sans-serif",
    body: "'IBM Plex Sans', system-ui, sans-serif",
    mono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace",
};
// ─────────────────────────── Garnet Core (back office) ───────────────────────────
export const CORE_TOKENS = {
    // surface (light)
    bgBase: '#f4f2ed',
    bgSurface: '#ffffff',
    bgElevated: '#f3f0ea',
    bgSunken: '#f7f5f0',
    border: '#e3e0d8',
    borderSubtle: '#ecebe4',
    borderHover: '#cdc9c0',
    borderActive: '#a8a4a0',
    // garnet red accent
    garnet: '#8f1d2c',
    garnetBright: '#b52336',
    garnetDim: '#6b1521',
    garnetMuted: '#fbeaec',
    // foreground
    fg1: '#1c1b20',
    fg2: '#62606b',
    fg3: '#9a97a2',
    // status
    statusPass: '#1f6b42',
    statusPassText: '#1a5e39',
    statusPassBg: '#e7f5ed',
    statusWarn: '#9a6210',
    statusWarnText: '#7d5010',
    statusWarnBg: '#fbf1de',
    statusFail: '#b52336',
    statusFailText: '#98202f',
    statusFailBg: '#fce9eb',
    statusInfo: '#1e4f8f',
    statusInfoText: '#1a4577',
    statusInfoBg: '#e8f0fb',
    // abstention (amber) — a designed state, not an error
    abstain: '#9a6210',
    abstainText: '#8a5a10',
    abstainBg: '#fbf1de',
    abstainBorder: '#ddc287',
    // coverage
    covCovered: '#1f6b42',
    covCoveredBg: '#eaf6ef',
    covCoveredBorder: '#c4e3d1',
    covThin: '#9a6210',
    covThinBg: '#fcf3e1',
    covThinBorder: '#ecd9a7',
    covEmpty: '#cdc9c0',
    covEmptyBg: '#f1efe9',
    // diff
    diffAddText: '#1a5e39',
    diffDelText: '#98202f',
};
export const CORE_TYPE = {
    display: "'DM Serif Display', Georgia, serif",
    body: "'DM Sans', system-ui, sans-serif",
    mono: "'JetBrains Mono', 'Fira Mono', monospace",
};
// ─────────────────────────── Shared scales ───────────────────────────
/** 4 → 64px spacing scale (identical in both token sets). */
export const SPACE = {
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
    12: '48px',
    16: '64px',
};
export const RADIUS = { sm: '3px', md: '4px', lg: '6px' };
//# sourceMappingURL=tokens.js.map